import { createFileRoute, Link } from "@tanstack/react-router";
import { NearMeServiceTable } from "../components/near-me-service-table";
import { Phone, ArrowRight, MapPin, Wrench, ChevronRight } from "lucide-react";
import { openQuoteModal } from "../components/quote-modal";
import { Reveal } from "../components/reveal";
import { Breadcrumbs } from "../components/breadcrumbs";
import { NAV_CITIES, NAV_SERVICES } from "../data/nav-items";
import { InlineQuoteSection } from "@/components/inline-quote-section";

const SITE = "https://capitalpropressurewashing.com";
const TITLE = "Pressure Washing Near Me | Albany & Capital Region NY";
const DESC =
  "Pressure washing, roof washing & soft washing near me — Capital Pro serves Albany, Troy, Clifton Park & the Capital Region. Free quotes from a local insured crew.";

const FAQS = [
  {
    q: "How do I find pressure washing near me?",
    a: "Look for a local, insured crew with reviews from your own town — not a national franchise. Capital Pro is based right here in the Capital Region and services Albany, Clifton Park, Saratoga Springs, Troy, Schenectady, and the surrounding towns directly, so you're always getting a local crew that knows the area's algae, pollen, and freeze-thaw conditions.",
  },
  {
    q: "Is pressure washing near me the same as soft washing?",
    a: "Not quite. Pressure washing uses high-pressure water alone, which can damage siding, shingles, and mortar. Soft washing uses low pressure with specialized cleaning solutions to kill mold, algae, and mildew at the root. We use the right method for each surface — soft wash for siding and roofs, higher pressure for concrete and pavers.",
  },
  {
    q: "How much does pressure washing near me typically cost?",
    a: "Pricing depends on the size of your home, the service, and the level of buildup. Most house washes, roof cleanings, and driveway cleanings in the Capital Region fall into a predictable range once we see photos or visit the property. Call or request a free quote and we'll give you an exact number — no surprise charges.",
  },
  {
    q: "Do local pressure washing companies near me offer discounts?",
    a: "We do — Capital Pro offers military and first responder discounts, plus savings when you bundle two or more services (like house washing and roof washing) in the same visit.",
  },
  {
    q: "How fast can a pressure washing company near me get to my house?",
    a: "We book jobs across the Capital Region every week and can often get you scheduled within days, sometimes the same week you call. Reach out and we'll give you our next available date for your town.",
  },
  {
    q: "Do you offer power washing services near me?",
    a: "Yes. Power washing and pressure washing describe the same thing to most homeowners, and we provide the full range across the Capital Region — house washing, roof cleaning, deck cleaning, driveway and paver cleaning, gutter cleaning, and commercial work. The difference that actually matters is method: siding and roofs get a low-pressure soft wash, while concrete and pavers get true pressure.",
  },
  {
    q: "Do you offer house washing services near me?",
    a: "Yes. House washing is our most requested service and we soft wash homes in Albany, Schenectady, Troy, Clifton Park, Saratoga Springs, Colonie, Latham, Niskayuna, Guilderland, Delmar, Ballston Spa, and Rensselaer. Most homes are finished in one to two hours.",
  },
  {
    q: "Where can I find deck cleaning near me?",
    a: "We clean wood, pressure-treated, cedar, and composite decks throughout the Capital Region. Deck cleaning uses a dedicated cleaner and low pressure so the boards come clean without fuzzing the grain, and we can quote sealing or staining alongside it.",
  },
  {
    q: "Who does roof washing near me?",
    a: "We do. Capital Pro soft washes roofs across the Capital Region — Albany, Schenectady, Troy, Clifton Park, Saratoga Springs, and the surrounding towns. Roof washing uses low pressure with a cleaning solution that kills the black streaks (gloeocapsa magma algae), moss, and lichen at the root, so shingles are never blasted with damaging high pressure.",
  },
  {
    q: "Is there soft washing near me in the Capital Region?",
    a: "Yes. Soft washing is the low-pressure method we use for every siding, roof, fence, and painted surface we clean. It kills mold, algae, and mildew at the root instead of blasting the surface, which is why the results last years rather than months.",
  },
  {
    q: "Who does concrete cleaning and sealing near me?",
    a: "We do. Capital Pro cleans and seals concrete driveways, patios, sidewalks, and pool decks across the Capital Region — Albany, Schenectady, Troy, Clifton Park, Saratoga Springs, Colonie, Latham, and the surrounding towns. We also specialize in stamped concrete: cleaning, re-sealing, and antiquing faded stamped concrete to restore its original color and pattern contrast.",
  },
  {
    q: "Who does stamped concrete restoration near me?",
    a: "Capital Pro restores stamped concrete across the Capital Region — Albany, Schenectady, Troy, Clifton Park, Saratoga Springs, Colonie, Latham, and nearby towns. If your stamped patio, walkway, or pool deck has faded, gone chalky, or lost its pattern contrast, we clean it, re-apply antiquing color, and re-seal it. Restoration brings back the original look for a fraction of the cost of tearing it out and re-pouring.",
  },
  {
    q: "Who does stamped concrete sealing near me?",
    a: "We do. Capital Pro cleans and seals stamped concrete across the Capital Region — Albany, Schenectady, Troy, Clifton Park, Saratoga Springs, Colonie, Latham, and the surrounding towns. Stamped concrete should be re-sealed every 2–3 years here; the sealer locks in the color and keeps water and road salt out of the surface. Wet-look/high-gloss or matte finish, whichever you prefer.",
  },
  {
    q: "Which commercial pressure washing companies near me service the Capital Region?",
    a: "Capital Pro handles commercial pressure washing across the region — storefronts and building exteriors, sidewalks and entryways, dumpster pads, awnings, fleet washing, heavy equipment, and barn and agricultural buildings. We carry a COI for your property manager and offer net-30 on approved accounts.",
  },
];

export const Route = createFileRoute("/pressure-washing-near-me")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/pressure-washing-near-me` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: `${SITE}/pressure-washing-near-me` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Capital Pro Pressure Washing",
          url: SITE,
          telephone: "+15189001913",
          priceRange: "$$",
          image: `${SITE}/__l5e/assets-v1/7fb348af-f23e-48db-88cd-6aeda94adc1a/hero-crew-surface-cleaner.jpg`,
          areaServed: NAV_CITIES.map((c) => c.city),
          address: {
            "@type": "PostalAddress",
            streetAddress: "5A Vatrano Rd",
            addressLocality: "Albany",
            addressRegion: "NY",
            postalCode: "12205",
            addressCountry: "US",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: NearMePage,
});

function MidCta({ heading }: { heading: string }) {
  return (
    <div className="mx-auto max-w-[820px] px-4 py-10 text-center">
      <p
        className="text-[var(--brand-deep-blue)] text-xl md:text-2xl uppercase"
        style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
      >
        {heading}
      </p>
      <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
        <button
          type="button"
          onClick={() => openQuoteModal()}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-[#C8102E] text-white px-6 py-3.5 uppercase tracking-wide hover:opacity-90 transition-opacity"
          style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
        >
          Get Free Quote <ArrowRight size={16} />
        </button>
        <a
          href="tel:+15189001913"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-[var(--brand-deep-blue)] text-white px-6 py-3.5 uppercase tracking-wide hover:opacity-90 transition-opacity"
          style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
        >
          <Phone size={16} strokeWidth={2.5} />
          (518) 900-1913
        </a>
      </div>
    </div>
  );
}

function NearMePage() {
  return (
    <main>
      <Breadcrumbs items={[{ label: "Pressure Washing Near Me" }]} />

      {/* Hero */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4 text-center">
          <p
            className="font-semibold uppercase tracking-[0.2em] text-sm mb-3"
            style={{ fontFamily: "var(--font-display)", color: "#ff6b7f" }}
          >
            Local & Family-Run
          </p>
          <h1
            className="text-3xl md:text-5xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Pressure Washing Near Me in Albany & the Capital Region
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Typed "pressure washing near me" or "power washing near me Albany"? You found a
            local crew — not a call center. Capital Pro has cleaned homes and businesses
            across the Capital Region for over 10 years, and we're often available for a
            free quote the same day you call.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Get Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
          <p className="mt-4 text-white/75 text-sm">
            5.0 stars from 125+ Google reviews · Fully insured · 10+ years local
          </p>
        </div>
      </section>

      {/* Intro copy */}
      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[820px] px-4 text-center">
          <p className="text-[var(--brand-charcoal)]/85 text-base md:text-lg leading-relaxed">
            When you search "pressure washing near me" or "power washing near me Albany," you
            want a company that actually shows up in your neighborhood — not a lead-generation
            site that resells your info. Capital Pro Pressure Washing is based right in the
            Capital Region. We're fully insured, we offer military and first-responder
            discounts, and our soft-wash system is safe for siding, roofs, and every surface
            around your home. Below you'll find every town and every service we offer — pick
            yours and get a free quote fast.
          </p>
        </div>
      </section>

      {/* City grid */}
      <section className="bg-[var(--brand-light-gray)] py-14 md:py-16">
        <div className="mx-auto max-w-[1000px] px-4">
          <div className="text-center mb-8 md:mb-10">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Near You
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Pick Your Town
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {NAV_CITIES.map((city, i) => (
              <Reveal key={city.slug} delayMs={i * 40} className="card-pop">
                <a
                  href={`/${city.slug}`}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 hover:border-[var(--brand-deep-blue)] transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-[var(--brand-deep-blue)]/10 text-[var(--brand-deep-blue)] flex items-center justify-center shrink-0">
                      <MapPin size={20} />
                    </div>
                    <span className="leading-tight">
                      <span
                        className="block text-[var(--brand-deep-blue)] text-base font-bold uppercase"
                        style={{ fontFamily: "var(--font-display)" }}
                      >
                        {city.city}
                      </span>
                      <span className="block text-[var(--brand-charcoal)]/60 text-xs mt-0.5">
                        Pressure washing in {city.city}, NY
                      </span>
                    </span>
                  </div>
                  <ChevronRight
                    size={20}
                    className="text-[var(--brand-charcoal)]/40 group-hover:text-[var(--brand-red)] transition-colors shrink-0"
                  />
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MidCta heading="Found your town? Get your free quote." />

      {/* Service grid */}
      <NearMeServiceTable />
      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[1000px] px-4">
          <div className="text-center mb-8 md:mb-10">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              What We Clean
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Pick Your Service
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {NAV_SERVICES.map((s, i) => (
              <Reveal key={s.slug} delayMs={i * 40} className="card-pop">
                <Link
                  to="/$service"
                  params={{ service: s.slug }}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-4 hover:border-[var(--brand-deep-blue)] hover:bg-white transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-[var(--brand-red)]/10 text-[var(--brand-red)] flex items-center justify-center shrink-0">
                      <Wrench size={20} />
                    </div>
                    <span
                      className="text-[var(--brand-deep-blue)] text-sm md:text-base font-bold uppercase leading-tight"
                      style={{ fontFamily: "var(--font-display)" }}
                    >
                      {s.name}
                    </span>
                  </div>
                  <ChevronRight
                    size={20}
                    className="text-[var(--brand-charcoal)]/40 group-hover:text-[var(--brand-red)] transition-colors shrink-0"
                  />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MidCta heading="Found your service? Pricing takes 60 seconds." />

      {/* How to pick */}
      <section className="bg-[var(--brand-light-gray)] py-14 md:py-16">
        <div className="mx-auto max-w-[820px] px-4">
          <div className="text-center mb-8">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Choosing Wisely
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              How to Pick a Local Pressure Washing Company
            </h2>
          </div>
          <div className="space-y-4 text-[var(--brand-charcoal)]/85 text-base leading-relaxed">
            <p>
              Not every company that shows up in a "near me" search actually operates locally.
              Before you book, ask a few simple questions: Are they insured? Do they use
              soft-wash for siding and roofs, or just blast everything with high pressure? Do
              they have real reviews from homeowners in your specific town? And do they answer
              the phone themselves, or route you through a national call center?
            </p>
            <p>
              Capital Pro is a local, fully insured crew that's worked across Albany,
              Schenectady, Saratoga Springs, Troy, Clifton Park, and the rest of the Capital
              Region for over 10 years. We know which method fits which surface, and we offer
              military and first-responder discounts as a thank you to the people who serve
              our community.
            </p>
          </div>
        </div>
      </section>

      {/* Search variations */}
      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[820px] px-4">
          <div className="text-center mb-8">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              However You Search For It
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Power Washing Services Near Me
            </h2>
          </div>
          <div className="space-y-4 text-[var(--brand-charcoal)]/85 text-base leading-relaxed">
            <p>
              Power washing and pressure washing mean the same thing to almost everyone
              searching for one. What actually matters is which method a company uses on
              which surface. Concrete, pavers, and masonry can take real pressure. Siding,
              roofs, decks, fences, and painted trim need a{" "}
              <Link to="/$service" params={{ service: "soft-washing" }} className="text-[var(--brand-bright-blue)] underline underline-offset-2">
                soft wash
              </Link>{" "}
              — low pressure plus a cleaning solution that kills the growth at the root.
            </p>
            <p>
              Here is what Capital Region homeowners search for most, and where each one goes:
            </p>
            <ul className="space-y-2 pl-1">
              {[
                { label: "House washing services near me", to: "house-washing", desc: "Soft-wash siding cleaning for vinyl, Hardie, brick, and stucco." },
                { label: "Deck cleaning near me", to: "deck-patio-cleaning", desc: "Wood, cedar, and composite deck cleaning without splintering." },
                { label: "Soft washing near me", to: "soft-washing", desc: "The low-pressure method behind every siding and roof clean we do." },
                { label: "Roof cleaning near me", to: "roof-washing", desc: "Black streaks, moss, and lichen removed shingle-safe." },
                { label: "Gutter cleaning near me", to: "gutter-cleaning", desc: "Hand-cleaned troughs, flushed downspouts, hardware inspected." },
                { label: "Commercial pressure washing companies near me", to: "commercial-pressure-washing", desc: "Storefronts, sidewalks, pads, fleets, and ag buildings." },
              ].map((row) => (
                <li key={row.to} className="flex gap-2">
                  <ChevronRight size={18} className="mt-1 shrink-0 text-[var(--brand-red)]" />
                  <span>
                    <Link
                      to="/$service"
                      params={{ service: row.to }}
                      className="font-semibold text-[var(--brand-bright-blue)] underline underline-offset-2"
                    >
                      {row.label}
                    </Link>{" "}
                    — {row.desc}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Roof washing & soft washing near me */}
      <section id="roof-washing-near-me" className="scroll-mt-24 bg-[var(--brand-light-gray)] py-14 md:py-16">
        <div className="mx-auto max-w-[820px] px-4">
          <div className="text-center mb-8">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Safe For Shingles
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Roof Washing Near Me
            </h2>
          </div>
          <div className="space-y-4 text-[var(--brand-charcoal)]/85 text-base leading-relaxed">
            <p>
              If you searched "roof washing near me" because of black streaks running down
              your shingles, that's an algae called gloeocapsa magma — it feeds on the
              limestone filler in shingles and it spreads across Capital Region neighborhoods
              every humid summer. Left alone it holds moisture against the shingle and
              shortens the roof's life.
            </p>
            <p>
              Our{" "}
              <Link to="/$service" params={{ service: "roof-washing" }} className="text-[var(--brand-bright-blue)] underline underline-offset-2">
                roof washing
              </Link>{" "}
              is a true soft wash: low pressure, a cleaning solution that kills the algae,
              moss, and lichen at the root, and no blasting that voids shingle warranties.
              Most roofs are treated in a few hours, and the streaks keep fading over the
              following weeks as the growth dies off completely.
            </p>
            <h3
              id="soft-washing-near-me"
              className="scroll-mt-24 pt-4 text-[var(--brand-deep-blue)] text-2xl md:text-3xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Soft Washing Near Me
            </h3>
            <p>
              The same{" "}
              <Link to="/$service" params={{ service: "soft-washing" }} className="text-[var(--brand-bright-blue)] underline underline-offset-2">
                soft washing
              </Link>{" "}
              method is what we use on siding, fences, and painted surfaces — so if you found
              us searching "soft washing near me," you're in the right place. One crew, one
              visit, every exterior surface cleaned with the method that's actually safe for
              it.
            </p>
            <p>
              Soft washing is the right call for vinyl and aluminum siding, painted wood,
              stucco, roofs, and fences — anywhere green mildew, black mold, or algae is
              growing. Instead of forcing dirt off with high pressure, the cleaning solution
              does the work and kills the growth at the root, so it doesn't come right back.
              For driveways, walkways, and pavers we switch to true{" "}
              <Link to="/" className="text-[var(--brand-bright-blue)] underline underline-offset-2">
                power washing in Albany
              </Link>{" "}
              and across the Capital Region.
            </p>
            <div className="pt-2 text-center">
              <button
                type="button"
                onClick={() => openQuoteModal()}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#C8102E] text-white px-6 py-3.5 uppercase tracking-wide hover:opacity-90 transition-opacity"
                style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
              >
                Get My Free Roof Quote <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Concrete cleaning & sealing near me */}
      <section id="concrete-cleaning-near-me" className="scroll-mt-24 bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[820px] px-4">
          <div className="text-center mb-8">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Driveways · Patios · Stamped Concrete
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Concrete Cleaning &amp; Sealing Near Me
            </h2>
          </div>
          <div className="space-y-4 text-[var(--brand-charcoal)]/85 text-base leading-relaxed">
            <p>
              If you searched "concrete cleaning near me" or "concrete sealing near me,"
              you're in the right place — we clean and seal driveways, patios, sidewalks,
              and pool decks across Albany and the entire Capital Region. Our commercial
              rotary surface cleaners leave an even, streak-free finish with no wand marks,
              and our penetrating sealers protect the slab against the road salt and
              freeze/thaw cycles that chew up unsealed concrete every winter here.
            </p>
            <p>
              We also specialize in stamped concrete restoration and sealing: cleaning, re-sealing, and antiquing
              faded stamped patios and walkways to bring back the original color and pattern
              contrast — a true color restoration, not just a clear coat over gray concrete.
              If your stamped concrete has faded or gone chalky, restoration costs a fraction
              of tearing it out and re-pouring. And if the color is still good, a stamped
              concrete sealing every 2–3 years is what keeps it that way through our winters.
              See the full process on our{" "}
              <Link to="/$service" params={{ service: "concrete-cleaning-sealing" }} className="text-[var(--brand-bright-blue)] underline underline-offset-2">
                concrete &amp; stamped concrete cleaning and sealing
              </Link>{" "}
              page, or ask about our{" "}
              <Link to="/concrete-sealing-freeze-thaw" className="text-[var(--brand-bright-blue)] underline underline-offset-2">
                freeze/thaw protection special
              </Link>{" "}
              before winter hits.
            </p>
            <div className="pt-2 text-center">
              <button
                type="button"
                onClick={() => openQuoteModal()}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#C8102E] text-white px-6 py-3.5 uppercase tracking-wide hover:opacity-90 transition-opacity"
                style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
              >
                Get My Free Concrete Quote <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[var(--brand-light-gray)] py-16 md:py-20">
        <div className="mx-auto max-w-[820px] px-4">
          <div className="text-center mb-10">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-3"
              style={{ fontFamily: "var(--font-display)" }}
            >
              FAQ
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f) => (
              <details
                key={f.q}
                className="group bg-white rounded-lg border border-gray-200 open:shadow-sm"
              >
                <summary className="cursor-pointer list-none px-5 py-4 font-semibold text-[var(--brand-deep-blue)] flex items-center justify-between gap-3">
                  {f.q}
                  <ChevronRight size={18} className="shrink-0 transition-transform group-open:rotate-90" />
                </summary>
                <p className="px-5 pb-4 text-[var(--brand-charcoal)]/85 text-sm leading-relaxed">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-12">
        <div className="mx-auto max-w-[1200px] px-4 text-center">
          <h2
            className="text-2xl md:text-3xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Ready for the Pressure Washing Crew Near You?
          </h2>
          <p className="mt-2 text-white/85">
            Fast, free quotes across the Capital Region. No pressure, no obligation.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Get Free Quote <ArrowRight size={18} />
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
        </div>
      </section>
      <InlineQuoteSection source="Near Me Hub" />
    </main>
  );
}
